import { CSSProperties, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as ChevronThick } from "../../../assets/svg/chevron-thick.svg";
import { COLORS, TEXT_COLORS } from "../../../constants/design/colors";
import FillBar from "../../FillMeter/FillBar";
import DropdownSimple from "../DropdownSimple";
import {
  PaginationContainer,
  ControlButton,
  ControlButtonContainer,
  GroupContainer,
} from "./style";
import { IHasMobileComponentProps } from "../../../pages/shared";

interface IPaginationProps extends IHasMobileComponentProps {
  // Starts from 0
  currentPage: number;
  totalPages: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  paginationName?: string;
  containerStyle?: CSSProperties;

  // If provided, show "Showing 1-25 out of 82 items"
  itemCountConfig?: {
    totalItems: number;
    perPage: number;
    perPageDropdownOptions?: number[];
    onPerPageDropdownSelect?: (perPage: number) => void;
  };
}

function Pagination({
  currentPage,
  totalPages,
  onPrevPage,
  onNextPage,
  containerStyle,
  itemCountConfig,
  paginationName,
  isMobile,
}: IPaginationProps) {
  const { t } = useTranslation("app", { keyPrefix: "shared.Pagination" });
  const startPage = currentPage + 1;
  const canPrev = currentPage !== 0;
  const canNext = currentPage < totalPages - 1;

  // An empty list has no first item. `perPage * 0 + 1` was 1 unconditionally
  // while the end clamped to totalItems, so zero rows read "Showing 1-0 out of
  // 0 items" -- a backwards range claiming to show an item that is not there.
  const hasItems = !!itemCountConfig && itemCountConfig.totalItems > 0;
  const startItemIndex = hasItems
    ? itemCountConfig!.perPage * currentPage + 1
    : 0;
  const endItemIndex = hasItems
    ? Math.min(
        startItemIndex + itemCountConfig!.perPage - 1,
        itemCountConfig!.totalItems
      )
    : 0;

  // Zero pages means there is no current page to be on.
  const displayPage = totalPages > 0 ? startPage : 0;

  const [showDropdown, setShowDropdown] = useState(false);

  const onToggleDropdown = useCallback(() => {
    setShowDropdown((prev) => !prev);
  }, []);

  return (
    <PaginationContainer style={containerStyle}>
      {!isMobile && (
        <GroupContainer style={{ justifyContent: "flex-start" }}>
          {itemCountConfig && (
            <span>
              {t("showing")}{" "}
              <strong>
                {startItemIndex}-{endItemIndex}
              </strong>{" "}
              {t("out_of")} <strong>{itemCountConfig.totalItems}</strong>{" "}
              {paginationName || t("items")}
            </span>
          )}
        </GroupContainer>
      )}
      <ControlButtonContainer>
        <ControlButton
          disabled={!canPrev}
          $direction="left"
          onClick={onPrevPage}
        >
          <ChevronThick />
        </ControlButton>
        {/* An empty list has no page 1 either. `startPage` is currentPage + 1
            unconditionally, so zero rows read "01" of "00" -- "page 1 of 0" --
            the same false claim as the item range above, three lines away from
            it, and missed when that one was fixed. Found by reading a live
            screenshot, not by a test. */}
        <span>{displayPage < 10 ? `0${displayPage}` : displayPage}</span>
        <FillBar
          fillColor={COLORS.highlight.one}
          // totalPages is 0 for an empty list, and startPage / 0 is Infinity.
          percent={totalPages > 0 ? (startPage / totalPages) * 100 : 0}
          style={{
            width: 64,
            height: 2,
          }}
        />
        <span style={{ color: TEXT_COLORS.three }}>
          {totalPages < 10 ? `0${totalPages}` : totalPages}
        </span>
        <ControlButton
          disabled={!canNext}
          $direction="right"
          onClick={onNextPage}
        >
          <ChevronThick />
        </ControlButton>
      </ControlButtonContainer>
      {!isMobile && (
        <GroupContainer style={{ justifyContent: "flex-end" }}>
          {itemCountConfig?.perPageDropdownOptions && (
            <>
              {t("items_per_page")}
              <DropdownSimple<string>
                dropDirection={"up"}
                show={showDropdown}
                onToggle={onToggleDropdown}
                title={String(itemCountConfig.perPage)}
                items={itemCountConfig.perPageDropdownOptions.map((v) =>
                  String(v)
                )}
                onSelectItem={(item) => {
                  itemCountConfig.onPerPageDropdownSelect?.(Number(item));
                }}
              />
            </>
          )}
        </GroupContainer>
      )}
    </PaginationContainer>
  );
}

export default Pagination;
